import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "./Reveal";
import { IconArrow } from "./icons";

const FEATURES = [
  {
    id: "greet",
    title: "Warm, supportive intake",
    desc: "Greets every patient calmly — in Hindi, English, or 20+ regional languages — and guides them step by step.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M8 10h.01M12 10h.01M16 10h.01M7 16c2.2-1.5 4.8-1.5 7 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M5 6h14a2 2 0 012 2v7a2 2 0 01-2 2H9l-4 3v-3H5a2 2 0 01-2-2V8a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: "calendar",
    title: "Live doctor calendar",
    desc: "Checks each doctor's OPD schedule, Google Calendar, and EMR slot inventory before offering times — no double booking.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8 14h3M13 14h3M8 17h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: "booking",
    title: "Book · reschedule · follow-up",
    desc: "Confirms appointments into your OPD queue, sends reminders, and captures visit reason for the front desk.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    ),
  },
];

const DOCTOR = {
  name: "Dr. Atchyut",
  specialty: "Orthopaedics",
  clinic: "Apple Ortho · Kurnool",
};

const SLOTS = [
  { time: "10:00 AM", open: true },
  { time: "10:30 AM", open: true },
  { time: "11:00 AM", open: true, selected: true },
  { time: "11:30 AM", open: false, label: "Full" },
];

const WEEK_DAYS = ["M", "T", "W", "T", "F", "S", "S"];
const WEEK_ACTIVE = 2; // Wednesday

function Waveform({ active }) {
  return (
    <div className={`voice-frontdesk-wave ${active ? "is-active" : ""}`} aria-hidden="true">
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <span key={i} style={{ animationDelay: `${i * 0.08}s` }} />
      ))}
    </div>
  );
}

function VoiceMockStack({ compact = false }) {
  const [phase, setPhase] = useState(0);
  const phases = ["listen", "check", "offer", "confirm"];

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setPhase(3);
      return undefined;
    }

    const delays = [0, 2800, 5200, 7800];
    const timers = delays.map((ms, i) => window.setTimeout(() => setPhase(i), ms));
    const loop = window.setInterval(() => {
      setPhase(0);
      delays.forEach((ms, i) => window.setTimeout(() => setPhase(i), ms));
    }, 12000);

    return () => {
      timers.forEach(clearTimeout);
      clearInterval(loop);
    };
  }, []);

  const step = phases[phase];
  const isListening = step === "listen";
  const isChecking = step === "check";
  const showSlots = step === "offer" || step === "confirm";
  const isConfirmed = step === "confirm";

  return (
    <div
      className={`voice-frontdesk-mock ${compact ? "is-compact" : ""}`}
      aria-hidden="true"
      data-phase={step}
    >
      <div className="voice-frontdesk-device">
        <div className="voice-frontdesk-device-bar">
          <span className="voice-frontdesk-live">
            <i className="voice-frontdesk-live-dot" />
            Live · AI Receptionist
          </span>
          <span className="voice-frontdesk-sync">
            {isChecking ? "Syncing calendar…" : "EMR + Calendar synced"}
          </span>
        </div>

        <div className="voice-frontdesk-call">
          <div className="voice-frontdesk-call-glow" />
          <div className="voice-frontdesk-call-main">
            <div className={`voice-frontdesk-orb-wrap ${isListening ? "is-listening" : ""}`}>
              <span className="voice-frontdesk-orb-ring" />
              <div className="voice-frontdesk-orb" />
            </div>
            <div className="voice-frontdesk-call-meta">
              <p className="voice-frontdesk-agent-label">Ananya · Reception AI</p>
              <p className="voice-frontdesk-caller">Speaking with Priya</p>
              <p className="voice-frontdesk-timer">02:32</p>
            </div>
          </div>
          <Waveform active={isListening} />
          <button type="button" className="voice-frontdesk-end" tabIndex={-1}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6.5 4.5c2.8-1.4 6.2-1.4 9 0l1.2.6a1.5 1.5 0 011 1.3v2.1a1.5 1.5 0 01-1.1 1.4l-1.4.4a12 12 0 01-5.4 0l-1.4-.4a1.5 1.5 0 01-1.1-1.4V6.4a1.5 1.5 0 011-1.3l1.2-.6z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path d="M9 14v2.5a3 3 0 006 0V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            End call
          </button>
        </div>

        <div className={`voice-frontdesk-calendar ${isChecking ? "is-loading" : ""}`}>
          <div className="voice-frontdesk-cal-head">
            <div>
              <strong>{DOCTOR.name}</strong>
              <span className="voice-frontdesk-cal-meta">{DOCTOR.specialty} · {DOCTOR.clinic}</span>
            </div>
            <span className="voice-frontdesk-cal-badge">OPD today</span>
          </div>

          <div className="voice-frontdesk-week" aria-hidden="true">
            {WEEK_DAYS.map((d, i) => (
              <span key={`${d}-${i}`} className={i === WEEK_ACTIVE ? "active" : ""}>
                {d}
              </span>
            ))}
          </div>

          <div className="voice-frontdesk-cal-status">
            {isChecking ? (
              <>
                <span className="voice-frontdesk-spinner" />
                Checking doctor calendar &amp; open OPD slots…
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                </svg>
                3 slots available · next opening 10:00 AM
              </>
            )}
          </div>

          <ul className="voice-frontdesk-cal-slots">
            {SLOTS.map((slot) => (
              <li
                key={slot.time}
                className={[
                  !slot.open ? "is-full" : "",
                  slot.selected && showSlots ? "is-selected" : "",
                  showSlots ? "is-visible" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <span>{slot.time}</span>
                <span>{slot.open ? (slot.selected && isConfirmed ? "Booked" : "Open") : slot.label}</span>
              </li>
            ))}
          </ul>

          {isConfirmed && (
            <div className="voice-frontdesk-confirm voice-frontdesk-confirm--inline">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              </svg>
              <div>
                <strong>11:00 AM confirmed</strong>
                <span>SMS sent · OPD queue</span>
              </div>
            </div>
          )}
        </div>

        <div className="voice-frontdesk-chat" aria-hidden="true">
          <div className="voice-frontdesk-chat-row is-user">
            <span className="voice-frontdesk-avatar user">P</span>
            <div className="voice-frontdesk-bubble user">
              <p>Hi, I need an appointment for knee pain this week.</p>
            </div>
          </div>

          <div className="voice-frontdesk-chat-row is-agent">
            <span className="voice-frontdesk-avatar agent">A</span>
            <div className="voice-frontdesk-bubble agent">
              <p>
                Of course, Priya — checking {DOCTOR.name}&apos;s calendar now.
              </p>
            </div>
          </div>

          {isChecking && (
            <div className="voice-frontdesk-system-chip">
              <span className="voice-frontdesk-spinner sm" />
              Reading EMR schedule &amp; Google Calendar…
            </div>
          )}

          {showSlots && (
            <>
              <div className="voice-frontdesk-chat-row is-agent">
                <span className="voice-frontdesk-avatar agent">A</span>
                <div className="voice-frontdesk-bubble agent">
                  <p>
                    Good news — we have openings today. Tap a time or tell me what suits you. I&apos;ll
                    send a confirmation SMS too.
                  </p>
                </div>
              </div>

              <div className="voice-frontdesk-slot-row">
                {SLOTS.filter((s) => s.open).map((slot) => (
                  <span key={slot.time} className={slot.selected ? "active" : ""}>
                    {slot.time}
                  </span>
                ))}
              </div>

              {isConfirmed && (
                <div className="voice-frontdesk-confirm">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                  <div>
                    <strong>11:00 AM confirmed</strong>
                    <span>Token will appear in OPD queue · SMS sent</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Competitor-style “always-on front desk” block — Clinical Noir styling.
 * variant: full (standalone section) | embed (inside Agent OS tab)
 */
export default function VoiceFrontDeskSection({ variant = "full", showCta = true }) {
  const isEmbed = variant === "embed";

  const content = (
    <div className={`voice-frontdesk-layout ${isEmbed ? "is-embed" : ""}`}>
      <Reveal className="voice-frontdesk-visual">
        <VoiceMockStack compact />
      </Reveal>

      <Reveal className="voice-frontdesk-copy">
        <h2
          className={isEmbed ? "voice-frontdesk-title-embed" : "section-title"}
          id={isEmbed ? undefined : "voice-frontdesk-heading"}
        >
          {isEmbed ? (
            <>
              Always-on AI front desk — <b>24/7 on your chart</b>
            </>
          ) : (
            <>
              Your receptionist AI checks the doctor calendar —{" "}
              <b>supportive, 24/7</b>
            </>
          )}
        </h2>

        {!isEmbed && (
          <p className="section-lede">
            Patients call, tap your website, or scan a QR code. The agent greets them warmly, reads live
            doctor schedules from your EMR, and books OPD slots — while your team stays in control.
          </p>
        )}

        <div className="voice-frontdesk-grid">
          {FEATURES.map((f) => (
            <article className="voice-frontdesk-card" key={f.id}>
              <span className="voice-frontdesk-card-icon">{f.icon}</span>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </article>
          ))}
        </div>

        <p className="voice-frontdesk-outcome">
          <strong>Outcome</strong> — more bookings, fewer missed calls, reception focused on patients in
          the clinic.
        </p>

        {showCta && !isEmbed && (
          <Link to="/demo" className="btn-primary voice-frontdesk-cta">
            See voice agent on your clinic <IconArrow />
          </Link>
        )}
      </Reveal>
    </div>
  );

  if (isEmbed) {
    return <div className="voice-frontdesk-embed">{content}</div>;
  }

  return (
    <section className="voice-frontdesk-section" aria-labelledby="voice-frontdesk-heading">
      <div className="wrap">{content}</div>
    </section>
  );
}
